/**
 * TenantThemeContext
 * ──────────────────
 * On mount: applies Operon defaults immediately (login page uses these).
 * After login: call refreshTheme() to fetch the authenticated tenant's branding
 * from GET /api/v1/me/theme and re-apply.
 *
 * Whitelabel admin flow:
 *   BACKEND_ADMIN  →  PATCH /api/v1/admin/tenants/{id}/theme
 *   { app_name, tagline, logo_url }
 */

import { DEFAULT_LOGO_URL } from '../components/BrandBadge'
import axios from 'axios'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

// ── Types ───────────────────────────────────────────────────────────────────

export interface ThemeConfig {
  appName: string
  tagline: string
  primaryColor: string
  logoUrl: string | null
}

interface ThemeContextValue extends ThemeConfig {
  refreshTheme: () => Promise<void>
}

// ── Defaults (Operon brand) ──────────────────────────────────────────────────

const DEFAULTS: ThemeConfig = {
  appName: 'Insurance Khareedo',
  tagline: 'by Ava India',
  primaryColor: '#7b74e2',
  logoUrl: DEFAULT_LOGO_URL,
}

// ── Colour helpers ───────────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const c = hex.replace('#', '')
  return [
    parseInt(c.slice(0, 2), 16),
    parseInt(c.slice(2, 4), 16),
    parseInt(c.slice(4, 6), 16),
  ]
}

function darken(hex: string, factor = 0.12): string {
  const [r, g, b] = hexToRgb(hex)
  const d = (v: number) =>
    Math.max(0, Math.round(v * (1 - factor))).toString(16).padStart(2, '0')
  return `#${d(r)}${d(g)}${d(b)}`
}

function lighten(hex: string, factor = 0.94): string {
  const [r, g, b] = hexToRgb(hex)
  const l = (v: number) =>
    Math.round(v + (255 - v) * factor).toString(16).padStart(2, '0')
  return `#${l(r)}${l(g)}${l(b)}`
}

// ── CSS injection ────────────────────────────────────────────────────────────

function applyTheme(config: ThemeConfig) {
  const root = document.documentElement
  const primary = config.primaryColor
  const [r, g, b] = hexToRgb(primary)
  const hover   = darken(primary, 0.12)
  const darker  = darken(primary, 0.22)
  const subtle  = lighten(primary, 0.94)
  // Theme-agnostic tint: translucent primary reads correctly on both light and
  // dark backgrounds. A solid lighten()→near-white value looks white in dark mode.
  const tint    = `rgba(${r},${g},${b},0.15)`

  root.style.setProperty('--brand-primary',        primary)
  root.style.setProperty('--brand-primary-hover',  hover)
  root.style.setProperty('--brand-primary-subtle', tint)
  root.style.setProperty('--brand-50',             subtle)
  root.style.setProperty('--brand-100',            lighten(primary, 0.88))
  root.style.setProperty('--brand-200',            lighten(primary, 0.76))
  root.style.setProperty('--brand-300',            lighten(primary, 0.58))
  root.style.setProperty('--brand-400',            lighten(primary, 0.36))
  root.style.setProperty('--brand-500',            lighten(primary, 0.14))
  root.style.setProperty('--brand-600',            primary)
  root.style.setProperty('--brand-700',            hover)
  root.style.setProperty('--brand-800',            darker)
  root.style.setProperty('--brand-900',            darken(primary, 0.40))
  root.style.setProperty('--border-focus',         primary)
  root.style.setProperty('--border-focus-ring',    `rgba(${r},${g},${b},0.20)`)
  root.style.setProperty('--sidebar-active-border',primary)
  root.style.setProperty('--sidebar-active',       `rgba(${r},${g},${b},0.14)`)

  if (config.appName) document.title = config.appName

  // Dynamic favicon — use dealer logo if available
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = config.logoUrl ?? '/favicon.ico'
}

function parseResponse(data: {
  app_name?: string
  tagline?: string
  primary_color?: string
  logo_url?: string | null
}): ThemeConfig {
  return {
    appName:      data.app_name      || DEFAULTS.appName,
    tagline:      data.tagline       || DEFAULTS.tagline,
    primaryColor: data.primary_color || DEFAULTS.primaryColor,
    logoUrl:      data.logo_url      ?? DEFAULTS.logoUrl,
  }
}

// ── Context ──────────────────────────────────────────────────────────────────

const TenantThemeContext = createContext<ThemeContextValue>({
  ...DEFAULTS,
  refreshTheme: async () => {},
})

export function TenantThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeConfig>(DEFAULTS)

  const refreshTheme = useCallback(async () => {
    const token = localStorage.getItem('access_token')
    if (!token) return
    try {
      const res = await axios.get('/api/v1/me/theme', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const resolved = parseResponse(res.data)
      applyTheme(resolved)
      setTheme(resolved)
    } catch {
      // Auth failure or network error — keep current theme
    }
  }, [])

  useEffect(() => {
    // Apply defaults immediately so the login page is never un-themed
    applyTheme(DEFAULTS)
    // If the user is already authenticated (e.g. page refresh), load their theme
    refreshTheme()
  }, [refreshTheme])

  return (
    <TenantThemeContext.Provider value={{ ...theme, refreshTheme }}>
      {children}
    </TenantThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  return useContext(TenantThemeContext)
}
